// src/front/src/pages/PendingPartExits.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import ConfirmDialog from "../components/ConfirmDialog";
import { Context } from "../store/appContext";
import "../styles/layout.css";
import "../styles/utilities.css";
import "../styles/forms.css";
import "../styles/pendingPartExits.css";

const STATUS_LABELS = {
  matched_exit: "Salida encontrada",
  pending_exit: "Pendiente de salida",
  pending_data: "Pendiente de datos",
  cancelled: "Anulado",
};

const OPERATIONAL_STATUS_LABELS = {
  balanced: "Correcto",
  not_found_in_orma: "No aparece en ORMA este mes",
  under_reported: "Faltan por dar de baja",
  over_reported: "Baja de más / revisar",
  pending_data: "Pendiente de datos",
  cancelled: "Anulado",
};

const CONTROLLED_ENTRY_STATE_LABELS = {
  NO_CONSUMIDA: "Sin consumir",
  PARCIAL: "Consumo parcial",
  PARCIALMENTE_CONSUMIDA: "Consumo parcial",
  TOTAL: "Consumida",
  TOTALMENTE_CONSUMIDA: "Consumida",
  ANULADA: "Anulada",
};

const getControlledEntryState = (entry) => {
  const backendState = String(
    entry?.entry_state || entry?.state || entry?.control_state || "",
  )
    .trim()
    .toUpperCase();

  if (backendState) return backendState;

  if (entry?.cancelled_at) return "ANULADA";

  const entered = Number(entry?.quantity_entered || 0);
  const remaining = Number(entry?.quantity_remaining || 0);

  if (remaining <= 0) return "TOTAL";
  if (remaining < entered) return "PARCIAL";
  return "NO_CONSUMIDA";
};

const getControlledEntryStateLabel = (entry) => {
  const state = getControlledEntryState(entry);

  return (
    CONTROLLED_ENTRY_STATE_LABELS[state] ||
    state ||
    "-"
  );
};

const getControlledEntryOriginalQuantity = (entry) =>
  Number(
    entry?.quantity_entered_original ??
      entry?.quantity_entered ??
      0,
  );

const getControlledEntryConsumedQuantity = (entry) => {
  if (
    entry?.quantity_consumed !== undefined &&
    entry?.quantity_consumed !== null
  ) {
    return Number(entry.quantity_consumed || 0);
  }

  return Math.max(
    Number(entry?.quantity_entered || 0) -
      Number(entry?.quantity_remaining || 0),
    0,
  );
};

const normalizeIdentity = (value) =>
  String(value || "").trim().toLocaleLowerCase("es-ES");

const SORT_FIELDS = [
  "material_id",
  "description",
  "quantity_expected",
  "scanned_at",
  "total_scanned_quantity",
  "total_matched_quantity",
  "difference_quantity",
  "operational_status",
  "last_exit_at",
];

const numberFormatter = new Intl.NumberFormat("es-ES", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const formatDateTime = (value) => {
  if (!value) return "-";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return parsed.toLocaleString("es-ES", {
    dateStyle: "short",
    timeStyle: "short",
  });
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return numberFormatter.format(Number(value || 0));
};

const getScannedDayKey = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getLocalDayKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getMonthKeyFromDayKey = (dayKey) =>
  dayKey && dayKey.length >= 7 ? dayKey.slice(0, 7) : "";

const getScannedMonthKey = (value) =>
  getMonthKeyFromDayKey(getScannedDayKey(value));

const formatDayLabel = (dayKey) => {
  if (!dayKey) return "Sin fecha";
  const parsed = new Date(`${dayKey}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return dayKey;
  return parsed.toLocaleDateString("es-ES", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const addDaysToDayKey = (dayKey, days) => {
  const baseDate = dayKey ? new Date(`${dayKey}T00:00:00`) : new Date();
  if (Number.isNaN(baseDate.getTime())) return getLocalDayKey(new Date());
  baseDate.setDate(baseDate.getDate() + days);
  return getLocalDayKey(baseDate);
};

const formatWorkOrders = (workOrders) =>
  Array.isArray(workOrders) && workOrders.length > 0 ? workOrders : [];

const normalizeSearchText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es-ES")
    .replace(/\s+/g, " ")
    .trim();

const rowMatchesSearch = (row, normalizedSearchTerm) => {
  if (!normalizedSearchTerm) return true;

  const searchableText = [
    row.material_id,
    row.description,
    formatWorkOrders(row.matched_work_orders).join(" "),
    row.orma_location,
    row.orma_column_location,
    row.orma_shelf,
    row.orma_bin,
    row.orma_second_location,
    row.orma_warehouse_id,
    row.orma_branch,
    row.orma_ax_group,
  ]
    .map(normalizeSearchText)
    .join(" ");

  return searchableText.includes(normalizedSearchTerm);
};

const renderOrmaLocation = (row) => {
  const status = row.orma_match_status;
  if (
    !status ||
    status === "not_found" ||
    status === "no_catalog" ||
    status === "empty_location"
  ) {
    return (
      <span className="pending-orma-location-none">Sin ubicación ORMA</span>
    );
  }
  if (status === "ambiguous") {
    return (
      <span className="pending-orma-ambiguous">Ubicación no unívoca</span>
    );
  }
  const segments = [];
  if (row.orma_column_location && String(row.orma_column_location).trim()) {
    segments.push(`Col. ${String(row.orma_column_location).trim()}`);
  }
  if (row.orma_shelf && String(row.orma_shelf).trim()) {
    segments.push(`Est. ${String(row.orma_shelf).trim()}`);
  }
  if (row.orma_bin && String(row.orma_bin).trim()) {
    segments.push(`Gab. ${String(row.orma_bin).trim()}`);
  }
  const locationText = segments.join(" ");
  if (!locationText) {
    return (
      <span className="pending-orma-location-none">Sin ubicación ORMA</span>
    );
  }
  return (
    <span className="pending-orma-location-main">{locationText}</span>
  );
};

const getOperationalStatus = (row) =>
  row.operational_status || row.status || "unknown";

const getOperationalStatusLabel = (row) => {
  const operationalStatus = getOperationalStatus(row);
  const baseLabel =
    OPERATIONAL_STATUS_LABELS[operationalStatus] ||
    STATUS_LABELS[row.status] ||
    operationalStatus ||
    "-";

  const differenceQuantity = Number(row.difference_quantity || 0);
  if (operationalStatus === "under_reported" && differenceQuantity > 0) {
    return `Faltan ${formatNumber(differenceQuantity)} por dar de baja`;
  }

  if (operationalStatus === "over_reported" && differenceQuantity > 0) {
    return `${baseLabel} ${formatNumber(differenceQuantity)}`;
  }

  return baseLabel;
};

const KEYPAD_DIGITS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];
const NUMERIC_KEYPAD_DIGITS = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "0",
];

const QuantityKeypadModal = ({
  open,
  mode,
  materialId,
  description,
  scannedAt,
  operationalDayKey,
  registeredQuantity,
  quantity,
  error,
  processing,
  onAppend,
  onBackspace,
  onClear,
  onCancel,
  onConfirm,
}) => {
  if (!open) return null;

  const parsedQuantity = Number.parseInt(quantity || "0", 10);
  const safeQuantity = Number.isNaN(parsedQuantity) ? 0 : parsedQuantity;
  const safeRegisteredQuantity = Number(registeredQuantity || 0);
  const isUndo = mode === "undo" || mode === "undo_latest";
  const canConfirm = /^[1-9]\d*$/.test(String(quantity || "").trim());
  const resultQuantity = isUndo
    ? Math.max(safeRegisteredQuantity - safeQuantity, 0)
    : safeQuantity;

  return (
    <div className="pending-quantity-modal-backdrop" role="presentation">
      <div
        className="pending-quantity-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pending_quantity_modal_title"
      >
        <div className="pending-quantity-modal-header">
          <h2 id="pending_quantity_modal_title">
            {isUndo ? "Anular unidades" : "Registrar cantidad"}
          </h2>
          <button
            type="button"
            className="pending-quantity-modal-close"
            onClick={onCancel}
            disabled={processing}
            aria-label="Cancelar"
          >
            <i className="bi bi-x-lg" aria-hidden="true"></i>
          </button>
        </div>

        <div className="pending-quantity-modal-details">
          <div>
            <span>Material</span>
            <strong>{materialId || "-"}</strong>
          </div>
          <div>
            <span>Descripción</span>
            <strong>{description || "-"}</strong>
          </div>
          <div>
            <span>Fecha operativa</span>
            <strong>
              {operationalDayKey
                ? formatDayLabel(operationalDayKey)
                : formatDateTime(scannedAt || new Date().toISOString())}
            </strong>
          </div>
        </div>

        <div className="pending-quantity-modal-totals">
          {isUndo && (
            <article>
              <span>Cantidad registrada</span>
              <strong>{formatNumber(safeRegisteredQuantity)}</strong>
            </article>
          )}
          <article>
            <span>{isUndo ? "Cantidad a anular" : "Cantidad a registrar"}</span>
            <strong>{quantity || "0"}</strong>
          </article>
          <article>
            <span>{isUndo ? "Cantidad resultante" : "Cantidad final"}</span>
            <strong>{formatNumber(resultQuantity)}</strong>
          </article>
        </div>

        {error && <div className="pending-quantity-modal-error">{error}</div>}

        <div className="pending-quantity-display" aria-live="polite">
          {quantity || "0"}
        </div>

        <div className="pending-quantity-keypad" role="group">
          {NUMERIC_KEYPAD_DIGITS.map((digit) => (
            <button
              key={digit}
              type="button"
              className="pending-quantity-key"
              onClick={() => onAppend(digit)}
              disabled={processing}
            >
              {digit}
            </button>
          ))}
        </div>

        <div className="pending-quantity-modal-actions">
          <button
            type="button"
            className="pending-quantity-action pending-quantity-backspace"
            onClick={onBackspace}
            disabled={processing || !quantity}
          >
            <i className="bi bi-backspace" aria-hidden="true"></i>
            Borrar
          </button>
          <button
            type="button"
            className="pending-quantity-action pending-quantity-clear"
            onClick={onClear}
            disabled={processing || !quantity}
          >
            <i className="bi bi-x-circle" aria-hidden="true"></i>
            Limpiar
          </button>
          <button
            type="button"
            className="pending-quantity-action pending-quantity-cancel"
            onClick={onCancel}
            disabled={processing}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="pending-quantity-action pending-quantity-confirm"
            onClick={onConfirm}
            disabled={processing || !canConfirm}
          >
            <i className="bi bi-check-lg" aria-hidden="true"></i>
            {processing ? "Procesando..." : "Confirmar"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ControlledEntryActionModal = ({
  modal,
  processing,
  onClose,
  onQuantityChange,
  onReasonChange,
  onConfirm,
}) => {
  if (!modal.open || !modal.entry) return null;

  const entry = modal.entry;
  const mode = modal.mode;
  const isCorrection = mode === "correction";
  const isCancellation = mode === "cancel";
  const isHistory = mode === "history";
  const state = getControlledEntryState(entry);
  const consumed = getControlledEntryConsumedQuantity(entry);
  const entered = Number(entry.quantity_entered || 0);
  const remaining = Number(entry.quantity_remaining || 0);
  const original = getControlledEntryOriginalQuantity(entry);
  const normalizedReason = String(modal.reason || "").trim();
  const parsedQuantity = Number.parseInt(
    String(modal.quantity || ""),
    10,
  );

  const quantityValid =
    !isCorrection ||
    (/^[1-9]\d*$/.test(String(modal.quantity || "").trim()) &&
      Number.isInteger(parsedQuantity));

  const reasonValid =
    !isCorrection && !isCancellation
      ? true
      : normalizedReason.length >= 3 &&
        normalizedReason.length <= 500;

  const canSubmit =
    !processing &&
    !isHistory &&
    quantityValid &&
    reasonValid;

  return (
    <div
      className="modal fade show d-block controlled-entry-action-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="controlled_entry_action_title"
      onMouseDown={onClose}
      style={{
        backgroundColor: "rgba(15, 23, 42, 0.62)",
      }}
    >
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h5
                className="modal-title"
                id="controlled_entry_action_title"
              >
                {isCorrection
                  ? "Corregir entrada"
                  : isCancellation
                    ? "Anular entrada"
                    : "Historial de correcciones"}
              </h5>
              <p className="mb-0 text-muted">
                {entry.material_id || "-"} ?{" "}
                {entry.description ||
                  entry.material_name ||
                  "Sin descripci?n"}
              </p>
            </div>

            <button
              type="button"
              className="btn-close"
              aria-label="Cerrar"
              onClick={onClose}
              disabled={processing}
            ></button>
          </div>

          <div className="modal-body">
            <div className="controlled-entry-modal-summary">
              <article>
                <span>Estado</span>
                <strong>{getControlledEntryStateLabel(entry)}</strong>
              </article>

              <article>
                <span>Original</span>
                <strong>{formatNumber(original)}</strong>
              </article>

              <article>
                <span>Efectiva</span>
                <strong>{formatNumber(entered)}</strong>
              </article>

              <article>
                <span>Consumida</span>
                <strong>{formatNumber(consumed)}</strong>
              </article>

              <article>
                <span>Restante</span>
                <strong>{formatNumber(remaining)}</strong>
              </article>
            </div>

            {isCorrection && (
              <div className="controlled-entry-modal-form">
                <div className="pending-form-field">
                  <label htmlFor="controlled_entry_new_quantity">
                    Nueva cantidad efectiva
                  </label>
                  <input
                    id="controlled_entry_new_quantity"
                    type="number"
                    inputMode="numeric"
                    min={Math.max(consumed, 1)}
                    value={modal.quantity}
                    onChange={(event) =>
                      onQuantityChange(event.target.value)
                    }
                    disabled={processing}
                    autoFocus
                  />
                  {state === "PARCIAL" ||
                  state === "PARCIALMENTE_CONSUMIDA" ? (
                    <small>
                      No puede ser inferior a la cantidad ya
                      consumida ({formatNumber(consumed)}) ni
                      superior a la cantidad efectiva actual (
                      {formatNumber(entered)}).
                    </small>
                  ) : (
                    <small>
                      Introduce la cantidad efectiva correcta de
                      esta entrada.
                    </small>
                  )}
                </div>

                <div className="pending-form-field">
                  <label htmlFor="controlled_entry_reason">
                    Motivo de la correcci?n
                  </label>
                  <textarea
                    id="controlled_entry_reason"
                    rows="4"
                    maxLength="500"
                    value={modal.reason}
                    onChange={(event) =>
                      onReasonChange(event.target.value)
                    }
                    disabled={processing}
                    placeholder="Indica el motivo de la correcci?n"
                  />
                  <small>
                    {normalizedReason.length}/500 ? m?nimo 3
                    caracteres
                  </small>
                </div>
              </div>
            )}

            {isCancellation && (
              <div className="controlled-entry-modal-form">
                <div className="alert alert-warning" role="alert">
                  La entrada quedar? anulada. Esta operaci?n solo
                  est? permitida mientras no exista consumo FIFO
                  asociado.
                </div>

                <div className="pending-form-field">
                  <label htmlFor="controlled_entry_cancel_reason">
                    Motivo de la anulaci?n
                  </label>
                  <textarea
                    id="controlled_entry_cancel_reason"
                    rows="4"
                    maxLength="500"
                    value={modal.reason}
                    onChange={(event) =>
                      onReasonChange(event.target.value)
                    }
                    disabled={processing}
                    placeholder="Indica el motivo de la anulaci?n"
                    autoFocus
                  />
                  <small>
                    {normalizedReason.length}/500 ? m?nimo 3
                    caracteres
                  </small>
                </div>
              </div>
            )}

            {isHistory && (
              <div className="controlled-entry-history">
                {modal.loadingHistory && (
                  <div className="pending-part-exits-state">
                    Cargando historial...
                  </div>
                )}

                {!modal.loadingHistory &&
                  modal.corrections.length === 0 &&
                  !modal.error && (
                    <div className="pending-part-exits-state">
                      Esta entrada no tiene correcciones
                      registradas.
                    </div>
                  )}

                {!modal.loadingHistory &&
                  modal.corrections.length > 0 && (
                    <div className="controlled-entry-history-list">
                      {modal.corrections.map(
                        (correction) => (
                          <article
                            className="controlled-entry-history-item"
                            key={correction.id}
                          >
                            <div>
                              <strong>
                                {correction.event_type ===
                                "CANCELLATION"
                                  ? "Anulaci?n"
                                  : "Correcci?n de cantidad"}
                              </strong>
                              <span>
                                {formatDateTime(
                                  correction.created_at,
                                )}
                              </span>
                            </div>

                            <p>
                              {correction.reason ||
                                "Sin motivo informado"}
                            </p>

                            <dl>
                              <div>
                                <dt>Efectiva</dt>
                                <dd>
                                  {formatNumber(
                                    correction.quantity_entered_before,
                                  )}{" "}
                                  ?{" "}
                                  {formatNumber(
                                    correction.quantity_entered_after,
                                  )}
                                </dd>
                              </div>

                              <div>
                                <dt>Restante</dt>
                                <dd>
                                  {formatNumber(
                                    correction.quantity_remaining_before,
                                  )}{" "}
                                  ?{" "}
                                  {formatNumber(
                                    correction.quantity_remaining_after,
                                  )}
                                </dd>
                              </div>

                              <div>
                                <dt>Consumida</dt>
                                <dd>
                                  {formatNumber(
                                    correction.quantity_consumed_at_action,
                                  )}
                                </dd>
                              </div>

                              <div>
                                <dt>Usuario</dt>
                                <dd>
                                  {correction.performed_by_username ||
                                    correction.performer_username ||
                                    correction.performed_by ||
                                    "-"}
                                </dd>
                              </div>
                            </dl>
                          </article>
                        ),
                      )}
                    </div>
                  )}
              </div>
            )}

            {modal.error && (
              <div
                className="alert alert-danger mt-3 mb-0"
                role="alert"
              >
                {modal.error}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onClose}
              disabled={processing}
            >
              Cerrar
            </button>

            {!isHistory && (
              <button
                type="button"
                className={
                  isCancellation
                    ? "btn btn-danger"
                    : "btn btn-primary"
                }
                onClick={onConfirm}
                disabled={!canSubmit}
              >
                {processing
                  ? "Procesando..."
                  : isCancellation
                    ? "Anular entrada"
                    : "Guardar correcci?n"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const PendingPartExits = () => {
  const { store, actions } = useContext(Context);
  const materialInputRef = useRef(null);
  const operatorInputRef = useRef(null);
  const preservedScrollYRef = useRef(null);
  const todayKey = getLocalDayKey(new Date());
  const [materialId, setMaterialId] = useState("");
  const [operatorCode, setOperatorCode] = useState("");
  const [activeOperatorCode, setActiveOperatorCode] = useState("");
  const [scannerSettingsReady, setScannerSettingsReady] = useState(false);
  const [isUpdatingScannerSettings, setIsUpdatingScannerSettings] =
    useState(false);
  const [quantityExpected, setQuantityExpected] = useState("1");
  const [scannerOperationMode, setScannerOperationMode] = useState("EXIT");
  const [entryPriority, setEntryPriority] = useState("NORMAL");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    field: "scanned_at",
    direction: "desc",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [undoingId, setUndoingId] = useState(null);
  const [isUndoingLatest, setIsUndoingLatest] = useState(false);
  const [isRevalidating, setIsRevalidating] = useState(false);
  const [showKeypad, setShowKeypad] = useState(false);
  const [selectedDayKey, setSelectedDayKey] = useState(todayKey);
  const [loadedMonthKey, setLoadedMonthKey] = useState(
    getMonthKeyFromDayKey(todayKey),
  );
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    confirmLabel: "Confirmar",
    onConfirm: null,
  });
  const [quantityModal, setQuantityModal] = useState({
    open: false,
    mode: "register",
    row: null,
    quantity: "",
    error: "",
  });

  const [workOrdersModal, setWorkOrdersModal] = useState({
    open: false,
    materialId: "",
    description: "",
    workOrders: [],
  });

  const [controlledEntryModal, setControlledEntryModal] = useState({
    open: false,
    mode: "correction",
    entry: null,
    quantity: "",
    reason: "",
    error: "",
    corrections: [],
    loadingHistory: false,
    processing: false,
  });

  const isAdmin = store.role === "admin";
  const isTechnician = store.role === "tecnico";
  const isScanner = store.role === "scanner";
  const currentUsername = normalizeIdentity(store.username);
  const isEntryMode = scannerOperationMode === "ENTRY";
  const isExitMode = scannerOperationMode === "EXIT";
  const scannerOperatorIdentificationConfigured = Boolean(
    store.scannerSettings?.require_operator_identification,
  );
  const operatorIdentificationRequired =
    isExitMode && scannerOperatorIdentificationConfigured;
  const scannerSettingsBlocked =
    isExitMode &&
    (!scannerSettingsReady || Boolean(store.scannerSettingsLoading));
  const normalizedActiveOperatorCode = activeOperatorCode.trim();
  const hasActiveOperator = Boolean(normalizedActiveOperatorCode);
  const effectiveMatchingScope = "historical";
  const currentMonthKey = getMonthKeyFromDayKey(todayKey);
  const selectedMonthKey = getMonthKeyFromDayKey(selectedDayKey);
  const rows = Array.isArray(store.pendingPartExits)
    ? store.pendingPartExits
    : [];
  const isKeypadVisible = showKeypad;
  const selectedOperationalDate =
    selectedDayKey && selectedDayKey !== todayKey ? selectedDayKey : "";
  const normalizedSearchTerm = normalizeSearchText(searchTerm);
  const previousDayKey = addDaysToDayKey(selectedDayKey, -1);
  const nextDayKey = addDaysToDayKey(selectedDayKey, 1);
  const canGoPrevDay = previousDayKey <= todayKey;
  const canGoNextDay = nextDayKey <= todayKey;

  const preserveScrollPosition = () => {
    preservedScrollYRef.current = window.scrollY;
  };

  const restorePreservedScrollPosition = () => {
    const preservedScrollY = preservedScrollYRef.current;
    if (preservedScrollY === null) return;

    window.requestAnimationFrame(() => {
      window.scrollTo({ top: preservedScrollY, behavior: "auto" });
      preservedScrollYRef.current = null;
    });
  };

  const focusScannerInput = ({ preventScroll = false } = {}) => {
    if (scannerSettingsBlocked) return;

    window.setTimeout(() => {
      const targetRef =
        operatorIdentificationRequired && !hasActiveOperator
          ? operatorInputRef
          : materialInputRef;

      targetRef.current?.focus({ preventScroll });
    }, 0);
  };

  const handleMaterialBlur = () => {
    if (!isScanner) return;

    window.setTimeout(() => {
      if (document.querySelector(
        ".pending-quantity-modal, .pending-work-orders-modal, .controlled-entry-action-modal",
      )) return;
      focusScannerInput({ preventScroll: true });
    }, 120);
  };

  useEffect(() => {
    actions.loadPendingPartExits({ matchingScope: effectiveMatchingScope });
  }, [store.token, effectiveMatchingScope]);

  useEffect(() => {
    if (!store.token || !isEntryMode) return;

    actions.loadControlledPartEntries();
  }, [store.token, isEntryMode]);

  useEffect(() => {
    let cancelled = false;

    if (!store.token) {
      setScannerSettingsReady(false);
      return () => {
        cancelled = true;
      };
    }

    setScannerSettingsReady(false);

    const loadSettings = async () => {
      const result = await actions.loadScannerSettings();

      if (!cancelled) {
        setScannerSettingsReady(Boolean(result?.success));
      }
    };

    loadSettings();

    return () => {
      cancelled = true;
    };
  }, [store.token]);

  useEffect(() => {
    if (!store.token || !selectedMonthKey) return;
    if (selectedMonthKey === loadedMonthKey) return;

    setLoadedMonthKey(selectedMonthKey);
  }, [store.token, loadedMonthKey, selectedMonthKey]);

  useEffect(() => {
    if (!store.token) return undefined;

    const intervalId = window.setInterval(
      () => {
        preserveScrollPosition();
        actions.loadPendingPartExits({
          matchingScope: effectiveMatchingScope,
          silent: true,
        });
        if (isScanner) {
          focusScannerInput({ preventScroll: true });
        }
      },
      15 * 60 * 1000,
    );

    return () => window.clearInterval(intervalId);
  }, [store.token, effectiveMatchingScope, isScanner, loadedMonthKey]);

  useEffect(() => {
    focusScannerInput();
  }, [
    isScanner,
    operatorIdentificationRequired,
    activeOperatorCode,
    scannerSettingsBlocked,
  ]);

  useEffect(() => {
    restorePreservedScrollPosition();
  }, [rows]);

  useEffect(() => {
    if (!isScanner) return undefined;

    const handleWindowFocus = () => {
      if (document.querySelector(
        ".pending-quantity-modal, .pending-work-orders-modal, .controlled-entry-action-modal",
      )) return;
      const active = document.activeElement;
      if (
        active &&
        ["INPUT", "SELECT", "TEXTAREA"].includes(active.tagName) &&
        active.id !== "pending_part_exit_material_id" &&
        active.id !== "pending_part_exit_operator_code"
      ) {
        return;
      }
      focusScannerInput();
    };
    window.addEventListener("focus", handleWindowFocus);
    return () => window.removeEventListener("focus", handleWindowFocus);
  }, [
    isScanner,
    operatorIdentificationRequired,
    activeOperatorCode,
    scannerSettingsBlocked,
  ]);

  const visibleRows = useMemo(() => {
    const filteredRows = rows.filter(
      (row) => getScannedMonthKey(row.scanned_at) === loadedMonthKey,
    );

    return [...filteredRows].sort((left, right) => {
      const leftValue = left[sortConfig.field];
      const rightValue = right[sortConfig.field];
      const direction = sortConfig.direction === "asc" ? 1 : -1;

      if (leftValue === rightValue) return 0;
      if (leftValue === null || leftValue === undefined) return 1;
      if (rightValue === null || rightValue === undefined) return -1;

      if (
        SORT_FIELDS.includes(sortConfig.field) &&
        sortConfig.field.endsWith("_at")
      ) {
        return (new Date(leftValue) - new Date(rightValue)) * direction;
      }

      if (typeof leftValue === "number" || typeof rightValue === "number") {
        return (Number(leftValue || 0) - Number(rightValue || 0)) * direction;
      }

      return (
        String(leftValue).localeCompare(String(rightValue), "es", {
          numeric: true,
          sensitivity: "base",
        }) * direction
      );
    });
  }, [loadedMonthKey, rows, sortConfig]);

  const searchedRows = useMemo(
    () =>
      visibleRows.filter((row) => rowMatchesSearch(row, normalizedSearchTerm)),
    [normalizedSearchTerm, visibleRows],
  );

  const pagedRows = useMemo(() => {
    if (normalizedSearchTerm) return searchedRows;
    if (!selectedDayKey) return searchedRows;
    return searchedRows.filter(
      (row) => getScannedDayKey(row.scanned_at) === selectedDayKey,
    );
  }, [normalizedSearchTerm, searchedRows, selectedDayKey]);

  const showDayPager = Boolean(selectedDayKey);

  const summary = useMemo(
    () =>
      pagedRows.reduce(
        (accumulator, row) => ({
          total: accumulator.total + 1,
          balanced:
            accumulator.balanced +
            (getOperationalStatus(row) === "balanced" ? 1 : 0),
          scanned: accumulator.scanned + Number(row.quantity_expected ?? 0),
          notFound:
            accumulator.notFound +
            (getOperationalStatus(row) === "not_found_in_orma" ? 1 : 0),
          underReported:
            accumulator.underReported +
            (getOperationalStatus(row) === "under_reported" ? 1 : 0),
          overReported:
            accumulator.overReported +
            (getOperationalStatus(row) === "over_reported" ? 1 : 0),
          pendingExit:
            accumulator.pendingExit + (row.status === "pending_exit" ? 1 : 0),
          pendingData:
            accumulator.pendingData +
            (getOperationalStatus(row) === "pending_data" ? 1 : 0),
          cancelled:
            accumulator.cancelled + (row.status === "cancelled" ? 1 : 0),
        }),
        {
          total: 0,
          scanned: 0,
          balanced: 0,
          notFound: 0,
          underReported: 0,
          overReported: 0,
          pendingExit: 0,
          pendingData: 0,
          cancelled: 0,
        },
      ),
    [pagedRows],
  );

  const materialDescription = useMemo(() => {
    const normalizedMaterialId = materialId.trim();
    if (!normalizedMaterialId) return "";
    const matchedRow = visibleRows.find(
      (row) => String(row.material_id || "").trim() === normalizedMaterialId,
    );
    return matchedRow?.description || "";
  }, [materialId, visibleRows]);

  const latestUndoRow = useMemo(
    () =>
      [...visibleRows]
        .filter((row) => Number(row.quantity_expected || 0) > 0)
        .sort((left, right) => {
          const leftDate = new Date(left.scanned_at || left.created_at || 0);
          const rightDate = new Date(right.scanned_at || right.created_at || 0);
          if (leftDate.getTime() !== rightDate.getTime()) {
            return rightDate - leftDate;
          }
          return Number(right.id || 0) - Number(left.id || 0);
        })[0] || null,
    [visibleRows],
  );
  const canUndoCurrentLoadedMonth =
    loadedMonthKey === currentMonthKey && Boolean(latestUndoRow);

  const validateModalQuantity = () => {
    const quantity = quantityModal.quantity.trim();
    if (!/^[1-9]\d*$/.test(quantity)) {
      return "La cantidad debe ser un entero positivo.";
    }

    const parsedQuantity = Number.parseInt(quantity, 10);
    if (quantityModal.mode === "undo" || quantityModal.mode === "undo_latest") {
      const registeredQuantity = Number(
        quantityModal.row?.quantity_expected || 0,
      );
      if (parsedQuantity > registeredQuantity) {
        return "No puedes anular más unidades de las registradas.";
      }
    }

    return "";
  };

  const openRegisterQuantityModal = () => {
    setQuantityModal({
      open: true,
      mode: "register",
      row: null,
      quantity: "",
      error: "",
    });
  };

  const openUndoQuantityModal = (row, latest = false) => {
    if (!row || Number(row.quantity_expected || 0) <= 0) {
      actions.showToast("No hay unidades activas para anular", "warning");
      focusScannerInput();
      return;
    }

    setQuantityModal({
      open: true,
      mode: latest ? "undo_latest" : "undo",
      row,
      quantity: "1",
      error: "",
    });
  };

  const closeQuantityModal = () => {
    if (isSubmitting || undoingId || isUndoingLatest) return;
    setQuantityModal((current) => ({ ...current, open: false, error: "" }));
    focusScannerInput({ preventScroll: true });
  };

  const openWorkOrdersModal = (row) => {
    const workOrders = formatWorkOrders(row?.matched_work_orders);

    if (workOrders.length === 0) {
      return;
    }

    setWorkOrdersModal({
      open: true,
      materialId: row?.material_id || "",
      description: row?.description || "",
      workOrders,
    });
  };

  const closeWorkOrdersModal = () => {
    setWorkOrdersModal({
      open: false,
      materialId: "",
      description: "",
      workOrders: [],
    });
    focusScannerInput({ preventScroll: true });
  };

  const handleQuantityModalAppend = (digit) => {
    setQuantityModal((current) => {
      const nextQuantity =
        current.quantity === "" && digit === "0"
          ? current.quantity
          : `${current.quantity}${digit}`;
      return { ...current, quantity: nextQuantity, error: "" };
    });
  };

  const handleQuantityModalBackspace = () => {
    setQuantityModal((current) => ({
      ...current,
      quantity: current.quantity.slice(0, -1),
      error: "",
    }));
  };

  const handleQuantityModalClear = () => {
    setQuantityModal((current) => ({ ...current, quantity: "", error: "" }));
  };

  const handleQuantityModalConfirm = async () => {
    if (isSubmitting || undoingId || isUndoingLatest) return;

    const validationError = validateModalQuantity();
    if (validationError) {
      setQuantityModal((current) => ({ ...current, error: validationError }));
      return;
    }

    const quantity = Number.parseInt(quantityModal.quantity, 10);

    if (quantityModal.mode === "register") {
      setQuantityExpected(String(quantity));
      setQuantityModal((current) => ({ ...current, open: false, error: "" }));
      focusScannerInput({ preventScroll: true });
      return;
    }

    const targetRow = quantityModal.row;
    if (!targetRow) return;

    preserveScrollPosition();
    if (quantityModal.mode === "undo_latest") {
      setIsUndoingLatest(true);
      const result = await actions.undoLatestPendingPartExit({
        matchingScope: effectiveMatchingScope,
        quantityToUndo: quantity,
      });
      setIsUndoingLatest(false);
      if (result?.success) {
        setQuantityModal((current) => ({ ...current, open: false }));
      } else {
        setQuantityModal((current) => ({
          ...current,
          error: result?.error || "No se pudo deshacer el escaneo.",
        }));
      }
    } else {
      setUndoingId(targetRow.id);
      const result = await actions.undoPendingPartExit(targetRow.id, {
        matchingScope: effectiveMatchingScope,
        quantityToUndo: quantity,
      });
      setUndoingId(null);
      if (result?.success) {
        setQuantityModal((current) => ({ ...current, open: false }));
      } else {
        setQuantityModal((current) => ({
          ...current,
          error: result?.error || "No se pudo devolver el registro.",
        }));
      }
    }
    restorePreservedScrollPosition();
    focusScannerInput({ preventScroll: true });
  };

  const closeControlledEntryModal = () => {
    if (controlledEntryModal.processing) return;

    setControlledEntryModal({
      open: false,
      mode: "correction",
      entry: null,
      quantity: "",
      reason: "",
      error: "",
      corrections: [],
      loadingHistory: false,
      processing: false,
    });

    focusScannerInput({ preventScroll: true });
  };

  const openControlledEntryCorrection = (entry) => {
    setControlledEntryModal({
      open: true,
      mode: "correction",
      entry,
      quantity: String(entry.quantity_entered || ""),
      reason: "",
      error: "",
      corrections: [],
      loadingHistory: false,
      processing: false,
    });
  };

  const openControlledEntryCancellation = (entry) => {
    setControlledEntryModal({
      open: true,
      mode: "cancel",
      entry,
      quantity: "",
      reason: "",
      error: "",
      corrections: [],
      loadingHistory: false,
      processing: false,
    });
  };

  const openControlledEntryHistory = async (entry) => {
    setControlledEntryModal({
      open: true,
      mode: "history",
      entry,
      quantity: "",
      reason: "",
      error: "",
      corrections: [],
      loadingHistory: true,
      processing: false,
    });

    const result =
      await actions.loadControlledPartEntryCorrections(entry.id);

    if (result?.success) {
      setControlledEntryModal((current) => ({
        ...current,
        corrections: Array.isArray(result.data)
          ? result.data
          : [],
        loadingHistory: false,
        error: "",
      }));
      return;
    }

    setControlledEntryModal((current) => ({
      ...current,
      corrections: [],
      loadingHistory: false,
      error:
        result?.error ||
        "No se pudo cargar el historial de la entrada.",
    }));
  };

  const handleControlledEntryQuantityChange = (value) => {
    setControlledEntryModal((current) => ({
      ...current,
      quantity: value,
      error: "",
    }));
  };

  const handleControlledEntryReasonChange = (value) => {
    setControlledEntryModal((current) => ({
      ...current,
      reason: value,
      error: "",
    }));
  };

  const handleControlledEntrySubmit = async () => {
    if (
      controlledEntryModal.processing ||
      !controlledEntryModal.entry
    ) {
      return;
    }

    const entry = controlledEntryModal.entry;
    const mode = controlledEntryModal.mode;
    const state = getControlledEntryState(entry);
    const consumed = getControlledEntryConsumedQuantity(entry);
    const entered = Number(entry.quantity_entered || 0);
    const reason = String(
      controlledEntryModal.reason || "",
    ).trim();

    if (reason.length < 3 || reason.length > 500) {
      setControlledEntryModal((current) => ({
        ...current,
        error:
          "El motivo debe contener entre 3 y 500 caracteres.",
      }));
      return;
    }

    let result = null;

    if (mode === "correction") {
      const rawQuantity = String(
        controlledEntryModal.quantity || "",
      ).trim();

      if (!/^[1-9]\d*$/.test(rawQuantity)) {
        setControlledEntryModal((current) => ({
          ...current,
          error:
            "La nueva cantidad debe ser un entero positivo.",
        }));
        return;
      }

      const newQuantity = Number.parseInt(
        rawQuantity,
        10,
      );

      if (newQuantity === entered) {
        setControlledEntryModal((current) => ({
          ...current,
          error:
            "La nueva cantidad debe ser distinta de la cantidad efectiva actual.",
        }));
        return;
      }

      if (
        (state === "PARCIAL" ||
          state === "PARCIALMENTE_CONSUMIDA") &&
        newQuantity < consumed
      ) {
        setControlledEntryModal((current) => ({
          ...current,
          error:
            "La cantidad no puede ser inferior a lo ya consumido.",
        }));
        return;
      }

      if (
        (state === "PARCIAL" ||
          state === "PARCIALMENTE_CONSUMIDA") &&
        newQuantity > entered
      ) {
        setControlledEntryModal((current) => ({
          ...current,
          error:
            "Una entrada parcialmente consumida no puede incrementarse.",
        }));
        return;
      }

      setControlledEntryModal((current) => ({
        ...current,
        processing: true,
        error: "",
      }));

      result =
        await actions.correctControlledPartEntry(
          entry.id,
          {
            newQuantity,
            reason,
            expectedUpdatedAt: entry.updated_at,
          },
        );
    } else if (mode === "cancel") {
      if (consumed > 0) {
        setControlledEntryModal((current) => ({
          ...current,
          error:
            "No se puede anular una entrada que ya tiene consumo.",
        }));
        return;
      }

      setControlledEntryModal((current) => ({
        ...current,
        processing: true,
        error: "",
      }));

      result =
        await actions.cancelControlledPartEntry(
          entry.id,
          {
            reason,
            expectedUpdatedAt: entry.updated_at,
          },
        );
    } else {
      return;
    }

    if (result?.success) {
      setControlledEntryModal({
        open: false,
        mode: "correction",
        entry: null,
        quantity: "",
        reason: "",
        error: "",
        corrections: [],
        loadingHistory: false,
        processing: false,
      });

      focusScannerInput({
        preventScroll: true,
      });

      return;
    }

    if (
      result?.status === 409 &&
      result?.code === "ENTRY_CHANGED"
    ) {
      setControlledEntryModal({
        open: false,
        mode: "correction",
        entry: null,
        quantity: "",
        reason: "",
        error: "",
        corrections: [],
        loadingHistory: false,
        processing: false,
      });

      actions.showToast(
        "La entrada ha cambiado desde que la abriste. Se han recargado los datos; revisa la entrada antes de volver a operar.",
        "warning",
      );

      return;
    }

    setControlledEntryModal((current) => ({
      ...current,
      processing: false,
      error:
        result?.error ||
        (mode === "cancel"
          ? "No se pudo anular la entrada."
          : "No se pudo corregir la entrada."),
    }));
  };

  const handleScannerOperationModeChange = (nextMode) => {
    if (nextMode !== "EXIT" && nextMode !== "ENTRY") return;
    if (nextMode === scannerOperationMode) return;

    setScannerOperationMode(nextMode);
    setMaterialId("");
    setQuantityExpected("1");
    setEntryPriority("NORMAL");

    window.setTimeout(() => {
      materialInputRef.current?.focus({
        preventScroll: true,
      });
    }, 0);
  };

  const handleOperatorSubmit = (event) => {
    event?.preventDefault();

    const normalizedOperatorCode = operatorCode.trim();

    if (!normalizedOperatorCode) {
      actions.showToast(
        "Escanea o introduce el código del operador.",
        "warning",
      );
      operatorInputRef.current?.focus();
      return;
    }

    setActiveOperatorCode(normalizedOperatorCode);
    setOperatorCode("");

    actions.showToast(
      `Operador ${normalizedOperatorCode} identificado`,
      "success",
    );

    window.setTimeout(() => {
      materialInputRef.current?.focus({
        preventScroll: true,
      });
    }, 0);
  };

  const handleChangeOperator = () => {
    setActiveOperatorCode("");
    setOperatorCode("");
    setMaterialId("");

    window.setTimeout(() => {
      operatorInputRef.current?.focus({
        preventScroll: true,
      });
    }, 0);
  };

  const handleToggleOperatorIdentification = async () => {
    if (
      !isAdmin ||
      isUpdatingScannerSettings ||
      scannerSettingsBlocked
    ) {
      return;
    }

    const nextValue = !scannerOperatorIdentificationConfigured;

    setIsUpdatingScannerSettings(true);

    const result = await actions.updateScannerSettings({
      requireOperatorIdentification: nextValue,
    });

    setIsUpdatingScannerSettings(false);

    if (!result?.success) {
      return;
    }

    setOperatorCode("");
    setActiveOperatorCode("");
    setMaterialId("");

    window.setTimeout(() => {
      if (nextValue) {
        operatorInputRef.current?.focus({
          preventScroll: true,
        });
      } else {
        materialInputRef.current?.focus({
          preventScroll: true,
        });
      }
    }, 0);
  };

  const handleSubmit = async (event) => {
    event?.preventDefault();

    const normalizedMaterialId = materialId.trim();
    const normalizedQuantity = String(quantityExpected || "").trim();

    if (scannerSettingsBlocked) {
      actions.showToast(
        "Espera a que se cargue la configuraci?n del scanner.",
        "warning",
      );
      return;
    }

    if (
      operatorIdentificationRequired &&
      !normalizedActiveOperatorCode
    ) {
      actions.showToast(
        "Identifica al operador antes de escanear el material.",
        "warning",
      );
      focusScannerInput();
      return;
    }

    if (!normalizedMaterialId) {
      actions.showToast("Introduce un ID MATERIAL", "warning");
      focusScannerInput();
      return;
    }

    if (!/^[1-9]\d*$/.test(normalizedQuantity)) {
      actions.showToast("La cantidad debe ser un entero positivo.", "warning");
      setQuantityExpected("1");
      focusScannerInput();
      return;
    }

    if (
      isEntryMode &&
      entryPriority !== "NORMAL" &&
      entryPriority !== "URGENT"
    ) {
      actions.showToast("Prioridad de entrada no v?lida.", "warning");
      setEntryPriority("NORMAL");
      focusScannerInput();
      return;
    }

    setIsSubmitting(true);
    preserveScrollPosition();

    const operationalDate = selectedOperationalDate;

    const result = isEntryMode
      ? await actions.createControlledPartEntry({
          materialId: normalizedMaterialId,
          quantity: normalizedQuantity,
          priority: entryPriority,
          notes: "",
          operationalDate: isScanner ? operationalDate : "",
        })
      : await actions.createPendingPartExit({
          materialId: normalizedMaterialId,
          quantityExpected: normalizedQuantity,
          matchingScope: effectiveMatchingScope,
          operationalDate,
          rfidCode: operatorIdentificationRequired
            ? normalizedActiveOperatorCode
            : "",
        });

    setIsSubmitting(false);

    if (result?.success) {
      if (
        isEntryMode &&
        result?.data?.material_data_status === "pending_data"
      ) {
        actions.showToast(
          "Entrada registrada. Pendiente de datos del material.",
          "warning",
        );
      }

      setMaterialId("");
      setQuantityExpected("1");

      if (isEntryMode) {
        setEntryPriority("NORMAL");
        await actions.loadControlledPartEntries();
      } else if (!operationalDate) {
        setSelectedDayKey(todayKey);
        setLoadedMonthKey(getMonthKeyFromDayKey(todayKey));
      }
    } else {
      actions.showToast(
        result?.error ||
          (isEntryMode
            ? "No se pudo registrar la entrada."
            : "No se pudo agregar el repuesto."),
        "danger",
      );
    }

    restorePreservedScrollPosition();
    focusScannerInput({ preventScroll: true });
  };

  const decrementQuantityExpected = () => {
    setQuantityExpected((currentQuantity) => {
      const parsedQuantity = Number.parseInt(currentQuantity || "1", 10);
      const safeQuantity = Number.isNaN(parsedQuantity) ? 1 : parsedQuantity;
      return String(Math.max(safeQuantity - 1, 1));
    });
  };

  const incrementQuantityExpected = () => {
    setQuantityExpected((currentQuantity) => {
      const parsedQuantity = Number.parseInt(currentQuantity || "1", 10);
      const safeQuantity = Number.isNaN(parsedQuantity) ? 1 : parsedQuantity;
      return String(safeQuantity + 1);
    });
  };

  const handleKeypadAppend = (character) => {
    setMaterialId((currentValue) => currentValue + character);
  };

  const handleKeypadBackspace = () => {
    setMaterialId((currentValue) => currentValue.slice(0, -1));
  };

  const handleKeypadClear = () => {
    setMaterialId("");
  };

  const handlePrevDay = () => {
    setSelectedDayKey((currentDayKey) =>
      addDaysToDayKey(currentDayKey || todayKey, -1),
    );
  };

  const handleNextDay = () => {
    setSelectedDayKey((currentDayKey) => {
      const candidateDayKey = addDaysToDayKey(currentDayKey, 1);
      return candidateDayKey <= todayKey ? candidateDayKey : currentDayKey;
    });
  };

  const handleSort = (field) => {
    setSortConfig((currentSort) => ({
      field,
      direction:
        currentSort.field === field && currentSort.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const handleUndoLatest = async () => {
    if (isUndoingLatest) return;
    openUndoQuantityModal(latestUndoRow, true);
  };

  const cancelConfirmDialog = () => {
    setConfirmDialog((current) => ({ ...current, open: false }));
    if (isScanner) {
      actions.showToast("Acción cancelada", "info");
    }
    focusScannerInput();
  };

  const handleUndo = async (row) => {
    if (undoingId) return;
    openUndoQuantityModal(row);
  };

  const handleRevalidate = async () => {
    if (!isAdmin || isRevalidating) return;

    setConfirmDialog({
      open: true,
      title: "Revalidar repuestos pendientes",
      message:
        "Se recalcularán los estados operativos de los registros visibles según las salidas actuales.",
      confirmLabel: "Revalidar",
      onConfirm: async () => {
        setConfirmDialog((current) => ({ ...current, open: false }));
        setIsRevalidating(true);
        await actions.revalidatePendingPartExits({
          matchingScope: effectiveMatchingScope,
        });
        setIsRevalidating(false);
        focusScannerInput();
      },
    });
  };

  return (
    <div className="container pending-part-exits-page scanner-pending-part-exits-page">
      <div className="page-header pending-part-exits-header">
        <div className="pending-part-exits-title">
          <span className="pending-part-exits-title-icon" aria-hidden="true">
            <i className="bi bi-clipboard-check"></i>
          </span>
          <div>
            <h1 className="page-title">
              {isEntryMode
                ? "Entradas de repuestos"
                : "Repuestos pendientes de baja"}
            </h1>
            <p className="page-subtitle">
              {isEntryMode
                ? "Terminal operativo de registro de repuestos recibidos."
                : "Terminal operativo de escaneo de repuestos retirados."}
            </p>
          </div>
        </div>

        {isAdmin && isExitMode && (
          <div className="pending-part-exits-header-actions">
            <button
              type="button"
              className={`btn pending-scanner-setting-toggle ${
                operatorIdentificationRequired
                  ? "btn-success"
                  : "btn-outline-secondary"
              }`}
              onClick={handleToggleOperatorIdentification}
              disabled={
                isUpdatingScannerSettings ||
                scannerSettingsBlocked
              }
              aria-pressed={operatorIdentificationRequired}
            >
              <i
                className="bi bi-person-badge me-1"
                aria-hidden="true"
              ></i>
              {isUpdatingScannerSettings
                ? "Actualizando..."
                : operatorIdentificationRequired
                  ? "Identificación: ACTIVADA"
                  : "Identificación: DESACTIVADA"}
            </button>

            <button
              type="button"
              className="btn btn-outline-secondary pending-part-exits-revalidate"
              onClick={handleUndoLatest}
              disabled={
                isUndoingLatest ||
                store.pendingPartExitsLoading ||
                !canUndoCurrentLoadedMonth
              }
            >
              <i
                className="bi bi-arrow-counterclockwise me-1"
                aria-hidden="true"
              ></i>
              {isUndoingLatest ? "Deshaciendo..." : "Deshacer último"}
            </button>
            <button
              type="button"
              className="btn btn-outline-primary pending-part-exits-revalidate"
              onClick={handleRevalidate}
              disabled={isRevalidating || store.pendingPartExitsLoading}
            >
              <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
              {isRevalidating ? "Revalidando..." : "Revalidar"}
            </button>
          </div>
        )}
        {!isAdmin && isExitMode && (
          <button
            type="button"
            className="btn btn-outline-secondary pending-part-exits-revalidate"
            onClick={handleUndoLatest}
            disabled={
                isUndoingLatest ||
                store.pendingPartExitsLoading ||
                !canUndoCurrentLoadedMonth
              }
          >
            <i
              className="bi bi-arrow-counterclockwise me-1"
              aria-hidden="true"
            ></i>
            {isUndoingLatest ? "Deshaciendo..." : "Deshacer último"}
          </button>
        )}
      </div>

      <section
        className="pending-scanner-operation-mode"
        aria-label="Tipo de operaci?n"
      >
        <button
          type="button"
          className={`btn ${
            isExitMode ? "btn-primary" : "btn-outline-primary"
          }`}
          onClick={() => handleScannerOperationModeChange("EXIT")}
          aria-pressed={isExitMode}
        >
          <i
            className="bi bi-box-arrow-up-right me-1"
            aria-hidden="true"
          ></i>
          SALIDAS
        </button>

        <button
          type="button"
          className={`btn ${
            isEntryMode ? "btn-success" : "btn-outline-success"
          }`}
          onClick={() => handleScannerOperationModeChange("ENTRY")}
          aria-pressed={isEntryMode}
        >
          <i
            className="bi bi-box-arrow-in-down me-1"
            aria-hidden="true"
          ></i>
          ENTRADAS
        </button>
      </section>

      {isExitMode && (
        <section className="pending-part-exits-summary" aria-label="Resumen">
                <article>
                  <span>Escaneadas</span>
                  <strong>{formatNumber(summary.scanned)}</strong>
                </article>
                <article className="summary-matched">
                  <span>Correctos</span>
                  <strong>{formatNumber(summary.balanced)}</strong>
                </article>
                <article className="summary-pending-exit">
                  <span>Pendientes</span>
                  <strong>{formatNumber(summary.underReported)}</strong>
                </article>
                <article className="summary-not-found">
                  <span>No aparecen ORMA</span>
                  <strong>{formatNumber(summary.notFound)}</strong>
                </article>
                <article className="summary-over-reported">
                  <span>Baja de más</span>
                  <strong>{formatNumber(summary.overReported)}</strong>
                </article>
              </section>
      )}

      {scannerSettingsBlocked && (
        <div
          className={`pending-part-exits-state ${
            store.scannerSettingsError
              ? "pending-part-exits-error"
              : ""
          }`}
          role="status"
        >
          {store.scannerSettingsError
            ? "No se pudo confirmar la configuraci?n del scanner."
            : "Cargando configuraci?n del scanner..."}
        </div>
      )}

      {operatorIdentificationRequired && (
        <section
          className={`pending-operator-panel ${
            hasActiveOperator
              ? "pending-operator-panel-active"
              : "pending-operator-panel-awaiting"
          }`}
          aria-label="Identificación del operador"
        >
          {hasActiveOperator ? (
            <>
              <div className="pending-operator-active">
                <span className="pending-operator-status-icon">
                  <i
                    className="bi bi-person-check-fill"
                    aria-hidden="true"
                  ></i>
                </span>

                <div className="pending-operator-active-copy">
                  <span>OPERADOR ACTIVO</span>
                  <strong>{normalizedActiveOperatorCode}</strong>
                  <small>
                    Los siguientes repuestos quedarán asociados
                    a este operador.
                  </small>
                </div>
              </div>

              <button
                type="button"
                className="btn btn-outline-secondary pending-operator-change"
                onClick={handleChangeOperator}
                disabled={isSubmitting}
              >
                <i
                  className="bi bi-arrow-repeat me-1"
                  aria-hidden="true"
                ></i>
                Cambiar operador
              </button>
            </>
          ) : (
            <>
              <form
                className="pending-operator-identification-form"
                onSubmit={handleOperatorSubmit}
              >
                <div className="pending-form-field pending-form-field-operator">
                  <label htmlFor="pending_part_exit_operator_code">
                    OPERADOR
                  </label>

                  <div className="pending-scanner-input-shell">
                    <i
                      className="bi bi-person-badge"
                      aria-hidden="true"
                    ></i>

                    <input
                      id="pending_part_exit_operator_code"
                      ref={operatorInputRef}
                      type="text"
                      value={operatorCode}
                      onChange={(event) =>
                        setOperatorCode(event.target.value)
                      }
                      placeholder="Escanea RFID o introduce código de operador"
                      autoComplete="off"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary pending-operator-confirm"
                >
                  <i
                    className="bi bi-person-check me-1"
                    aria-hidden="true"
                  ></i>
                  Identificar
                </button>
              </form>

              <p className="pending-operator-help">
                Identifica primero al operador. El código puede
                escribirse manualmente o recibirse desde un lector
                RFID que funcione como teclado.
              </p>
            </>
          )}
        </section>
      )}

      <form className={`pending-part-exits-form ${
          isEntryMode ? "pending-part-exits-form-entry" : ""
        }`} onSubmit={handleSubmit}>
        <div className="pending-form-field pending-form-field-material">
          <label htmlFor="pending_part_exit_material_id">ID MATERIAL</label>
          <div className="pending-scanner-input-shell">
            <i className="bi bi-upc-scan" aria-hidden="true"></i>
            <input
              id="pending_part_exit_material_id"
              ref={materialInputRef}
              type="text"
              value={materialId}
              onChange={(event) => setMaterialId(event.target.value)}
              placeholder={
                operatorIdentificationRequired && !hasActiveOperator
                  ? "Identifica primero al operador"
                  : "Escanea o introduce ID MATERIAL"
              }
              autoComplete="off"
              disabled={
                scannerSettingsBlocked ||
                (operatorIdentificationRequired && !hasActiveOperator)
              }
              onBlur={handleMaterialBlur}
            />
          </div>
        </div>

        <div className="pending-form-field pending-form-field-quantity">
          <label htmlFor="pending_part_exit_quantity">Cantidad escaneada</label>
          <div className="pending-quantity-stepper">
            <button
              type="button"
              className="pending-quantity-step pending-quantity-step-minus"
              onClick={decrementQuantityExpected}
              disabled={Number(quantityExpected || 1) <= 1}
              aria-label="Disminuir cantidad"
            >
              <i className="bi bi-dash-lg" aria-hidden="true"></i>
            </button>
            <input
              id="pending_part_exit_quantity"
              type="text"
              inputMode="numeric"
              value={formatNumber(quantityExpected)}
              readOnly
              aria-label="Cantidad actual"
            />
            <button
              type="button"
              className="pending-quantity-step pending-quantity-step-plus"
              onClick={incrementQuantityExpected}
              aria-label="Aumentar cantidad"
            >
              <i className="bi bi-plus-lg" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              className="pending-quantity-open"
              onClick={openRegisterQuantityModal}
              aria-label="Abrir teclado numerico de cantidad"
            >
              <i className="bi bi-calculator" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        {isEntryMode && (
          <div className="pending-form-field pending-form-field-priority">
            <label htmlFor="controlled_part_entry_priority">
              Prioridad
            </label>
            <select
              id="controlled_part_entry_priority"
              value={entryPriority}
              onChange={(event) =>
                setEntryPriority(event.target.value)
              }
              disabled={isSubmitting}
            >
              <option value="NORMAL">NORMAL</option>
              <option value="URGENT">URGENTE</option>
            </select>
          </div>
        )}

        <button
          className="btn btn-primary pending-part-exits-add"
          type="submit"
          disabled={
            isSubmitting ||
            store.pendingPartExitsLoading ||
            scannerSettingsBlocked ||
            (operatorIdentificationRequired && !hasActiveOperator)
          }
        >
          <i className="bi bi-plus-lg me-1" aria-hidden="true"></i>
          {isSubmitting ? "Agregando..." : "Agregar"}
        </button>
      </form>

      {isEntryMode && (
        <section
          className="pending-part-exits-card controlled-part-entries-card"
          aria-label="Entradas de repuestos registradas"
        >
          <div className="pending-part-exits-card-head">
            <div>
              <h2>Control de entradas</h2>
              <p>
                {Array.isArray(store.controlledPartEntries)
                  ? `${formatNumber(store.controlledPartEntries.length)} entradas registradas.`
                  : "0 entradas registradas."}
              </p>
            </div>
          </div>

          {store.controlledPartEntriesLoading && (
            <div className="pending-part-exits-state">
              Cargando entradas...
            </div>
          )}

          {!store.controlledPartEntriesLoading &&
            store.controlledPartEntriesError && (
              <div className="pending-part-exits-state pending-part-exits-state-error">
                {store.controlledPartEntriesError}
              </div>
            )}

          {!store.controlledPartEntriesLoading &&
            !store.controlledPartEntriesError &&
            (!Array.isArray(store.controlledPartEntries) ||
              store.controlledPartEntries.length === 0) && (
              <div className="pending-part-exits-state">
                Sin entradas registradas.
              </div>
            )}

          {!store.controlledPartEntriesLoading &&
            !store.controlledPartEntriesError &&
            Array.isArray(store.controlledPartEntries) &&
            store.controlledPartEntries.length > 0 && (
              <div className="pending-table-scroll-wrapper">
                <div
                  className="pending-part-exits-table controlled-part-entries-table"
                  role="table"
                  aria-label="Hist?rico de entradas controladas"
                >
                  <div
                    className="pending-part-exits-table-head"
                    role="row"
                  >
                    <div role="columnheader">ID MATERIAL</div>
                    <div role="columnheader">Descripci?n</div>
                    <div role="columnheader">Original</div>
                    <div role="columnheader">Efectiva</div>
                    <div role="columnheader">Consumida</div>
                    <div role="columnheader">Restante</div>
                    <div role="columnheader">Estado</div>
                    <div role="columnheader">Prioridad</div>
                    <div role="columnheader">Fecha entrada</div>
                    <div role="columnheader">Usuario</div>
                    <div role="columnheader">Acciones</div>
                  </div>

                  {store.controlledPartEntries.map((entry) => {
                    const state = getControlledEntryState(entry);
                    const consumed =
                      getControlledEntryConsumedQuantity(entry);
                    const entryOwner = normalizeIdentity(
                      entry.created_by_username,
                    );
                    const isOwnEntry =
                      Boolean(currentUsername) &&
                      Boolean(entryOwner) &&
                      currentUsername === entryOwner;
                    const isUnconsumed =
                      state === "NO_CONSUMIDA";
                    const isPartial =
                      state === "PARCIAL" ||
                      state === "PARCIALMENTE_CONSUMIDA";
                    const canCorrect =
                      (isUnconsumed &&
                        (isAdmin ||
                          ((isScanner || isTechnician) &&
                            isOwnEntry))) ||
                      (isPartial && isAdmin);
                    const canCancel =
                      isUnconsumed &&
                      consumed === 0 &&
                      (isAdmin ||
                        ((isScanner || isTechnician) &&
                          isOwnEntry));

                    return (
                      <div
                        className={`pending-part-exits-row controlled-part-entry-row controlled-part-entry-row-${state.toLocaleLowerCase(
                          "es-ES",
                        )}`}
                        role="row"
                        key={entry.id}
                      >
                        <div
                          role="cell"
                          data-label="ID MATERIAL"
                        >
                          <strong>{entry.material_id || "-"}</strong>
                        </div>

                        <div
                          role="cell"
                          data-label="Descripci?n"
                        >
                          {entry.material_data_status === "pending_data" ? (
                            <span className="controlled-entry-pending-data">
                              Pendiente de datos
                            </span>
                          ) : (
                            entry.description ||
                            entry.material_name ||
                            "-"
                          )}
                        </div>

                        <div
                          role="cell"
                          data-label="Original"
                        >
                          {formatNumber(
                            getControlledEntryOriginalQuantity(
                              entry,
                            ),
                          )}
                        </div>

                        <div
                          role="cell"
                          data-label="Efectiva"
                        >
                          {formatNumber(
                            entry.quantity_entered,
                          )}
                        </div>

                        <div
                          role="cell"
                          data-label="Consumida"
                        >
                          {formatNumber(consumed)}
                        </div>

                        <div
                          role="cell"
                          data-label="Restante"
                        >
                          {formatNumber(
                            entry.quantity_remaining,
                          )}
                        </div>

                        <div
                          role="cell"
                          data-label="Estado"
                        >
                          <span
                            className={`controlled-entry-state controlled-entry-state-${state.toLocaleLowerCase(
                              "es-ES",
                            )}`}
                          >
                            {getControlledEntryStateLabel(
                              entry,
                            )}
                          </span>

                          {entry.is_corrected && (
                            <small className="controlled-entry-corrected-label">
                              Corregida
                            </small>
                          )}
                        </div>

                        <div
                          role="cell"
                          data-label="Prioridad"
                        >
                          <span
                            className={`controlled-entry-priority controlled-entry-priority-${
                              entry.priority === "URGENT"
                                ? "urgent"
                                : "normal"
                            }`}
                          >
                            {entry.priority === "URGENT"
                              ? "URGENTE"
                              : "NORMAL"}
                          </span>
                        </div>

                        <div
                          role="cell"
                          data-label="Fecha entrada"
                        >
                          {formatDateTime(entry.scanned_at)}
                        </div>

                        <div
                          role="cell"
                          data-label="Usuario"
                        >
                          {entry.created_by_username ||
                            entry.created_by ||
                            "-"}
                        </div>

                        <div
                          className="controlled-entry-actions"
                          role="cell"
                          data-label="Acciones"
                        >
                          {canCorrect && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() =>
                                openControlledEntryCorrection(
                                  entry,
                                )
                              }
                            >
                              Corregir
                            </button>
                          )}

                          {canCancel && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() =>
                                openControlledEntryCancellation(
                                  entry,
                                )
                              }
                            >
                              Anular
                            </button>
                          )}

                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() =>
                              openControlledEntryHistory(entry)
                            }
                          >
                            Historial
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
        </section>
      )}

      {isExitMode && (
        <section
          className="pending-part-exits-filters"
          aria-label="Filtros de fecha"
        >
          <div className="pending-form-field">
            <label htmlFor="pending_part_exit_month">Mes visible</label>
            <input
              id="pending_part_exit_month"
              type="month"
              value={loadedMonthKey}
              max={currentMonthKey}
              onChange={(event) => {
                const nextMonthKey = event.target.value;
                if (!nextMonthKey) return;
                setLoadedMonthKey(nextMonthKey);
                setSelectedDayKey("");
                setSearchTerm("");
              }}
            />
          </div>

          <div className="pending-form-field">
            <label htmlFor="pending_part_exit_day">Día operativo</label>
            <input
              id="pending_part_exit_day"
              type="date"
              value={selectedDayKey}
              max={todayKey}
              onChange={(event) => {
                const nextDayKey = event.target.value;
                setSelectedDayKey(nextDayKey);
                if (nextDayKey) {
                  setLoadedMonthKey(getMonthKeyFromDayKey(nextDayKey));
                }
              }}
            />
          </div>

          <button
            type="button"
            className="btn btn-outline-secondary pending-filters-clear"
            onClick={() => setSelectedDayKey("")}
          >
            Ver mes completo
          </button>

          <button
            type="button"
            className="btn btn-outline-primary pending-filters-clear"
            onClick={() => {
              setSelectedDayKey(todayKey);
              setLoadedMonthKey(currentMonthKey);
            }}
          >
            Hoy
          </button>
        </section>

      )}

      {isEntryMode && isScanner && (
        <section
          className="pending-part-exits-filters pending-controlled-entry-operational-date"
          aria-label="D?a operativo de entrada"
        >
          <div className="pending-form-field">
            <label htmlFor="controlled_part_entry_operational_day">
              D?a operativo
            </label>
            <input
              id="controlled_part_entry_operational_day"
              type="date"
              value={selectedDayKey}
              max={todayKey}
              onChange={(event) => {
                const nextDayKey = event.target.value;
                setSelectedDayKey(nextDayKey || todayKey);
              }}
            />
          </div>

          <button
            type="button"
            className="btn btn-outline-primary pending-filters-clear"
            onClick={() => setSelectedDayKey(todayKey)}
          >
            Hoy
          </button>
        </section>
      )}

      {!isKeypadVisible && (
        <button
          type="button"
          className="btn btn-success pending-keypad-toggle"
          onClick={() => setShowKeypad(true)}
        >
          <i className="bi bi-keyboard me-1" aria-hidden="true"></i>
          Mostrar teclado
        </button>
      )}

      {isKeypadVisible && (
        <div
          className="pending-keypad"
          role="group"
          aria-label="Teclado para ID MATERIAL"
        >
          <div className="pending-keypad-bar">
            <span className="pending-keypad-bar-title">
              <i className="bi bi-keyboard me-1" aria-hidden="true"></i>
              Teclado
            </span>
            <button
              type="button"
              className="pending-keypad-hide"
              onClick={() => setShowKeypad(false)}
            >
              <i className="bi bi-chevron-up me-1" aria-hidden="true"></i>
              Ocultar teclado
            </button>
          </div>
          <div className="pending-keypad-keys">
            {KEYPAD_DIGITS.map((digit) => (
              <button
                key={digit}
                type="button"
                className="pending-keypad-key"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleKeypadAppend(digit)}
              >
                {digit}
              </button>
            ))}
            <button
              type="button"
              className="pending-keypad-key pending-keypad-key-letter"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleKeypadAppend("U")}
            >
              U
            </button>
            <button
              type="button"
              className="pending-keypad-key"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleKeypadAppend("0")}
            >
              0
            </button>
            <button
              type="button"
              className="pending-keypad-key pending-keypad-key-letter"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleKeypadAppend("R")}
            >
              R
            </button>
          </div>
          <div className="pending-keypad-actions">
            <button
              type="button"
              className="pending-keypad-action pending-keypad-backspace"
              onMouseDown={(event) => event.preventDefault()}
              onClick={handleKeypadBackspace}
            >
              <i className="bi bi-backspace" aria-hidden="true"></i>
              Borrar
            </button>
            <button
              type="button"
              className="pending-keypad-action pending-keypad-clear"
              onMouseDown={(event) => event.preventDefault()}
              onClick={handleKeypadClear}
            >
              <i className="bi bi-x-lg" aria-hidden="true"></i>
              Limpiar
            </button>
            <button
              type="button"
              className="pending-keypad-action pending-keypad-enter"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleSubmit()}
              disabled={isSubmitting || store.pendingPartExitsLoading}
            >
              <i className="bi bi-check-lg" aria-hidden="true"></i>
              Validar
            </button>
          </div>
        </div>
      )}

      {isExitMode && showDayPager && (
        <div
          className="pending-day-pager"
          role="navigation"
          aria-label="Navegacion por dia de escaneo"
        >
          <button
            type="button"
            className="pending-day-pager-btn"
            onClick={handlePrevDay}
            disabled={!canGoPrevDay}
          >
            <i className="bi bi-chevron-left" aria-hidden="true"></i>
            Día anterior
          </button>
          <div className="pending-day-pager-current">
            <span className="pending-day-pager-date">
              {formatDayLabel(selectedDayKey)}
            </span>
            <span className="pending-day-pager-count">
              {formatNumber(pagedRows.length)} escaneos
            </span>
          </div>
          <button
            type="button"
            className="pending-day-pager-btn"
            onClick={handleNextDay}
            disabled={!canGoNextDay}
          >
            Día siguiente
            <i className="bi bi-chevron-right" aria-hidden="true"></i>
          </button>
        </div>
      )}

      {isExitMode && (
      <section className="pending-part-exits-card">
        <div className="pending-part-exits-card-head">
          <div>
            <h2>Control de salidas</h2>
            <p>
              {normalizedSearchTerm
                ? `${formatNumber(pagedRows.length)} resultados de ${formatNumber(visibleRows.length)} escaneos del mes`
                : `${formatNumber(pagedRows.length)} registros visibles.`}
            </p>
          </div>
          <div className="pending-part-exits-search">
            <label htmlFor="pending_part_exit_search">Buscar</label>
            <div className="pending-part-exits-search-shell">
              <i className="bi bi-search" aria-hidden="true"></i>
              <input
                id="pending_part_exit_search"
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Artículo, descripción, OT o ubicación"
                autoComplete="off"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  aria-label="Limpiar búsqueda"
                >
                  <i className="bi bi-x-lg" aria-hidden="true"></i>
                </button>
              )}
            </div>
          </div>
        </div>

        {store.pendingPartExitsLoading && (
          <div className="pending-part-exits-state">Cargando registros...</div>
        )}

        {!store.pendingPartExitsLoading && store.pendingPartExitsError && (
          <div className="pending-part-exits-state pending-part-exits-error">
            {store.pendingPartExitsError}
          </div>
        )}

        {!store.pendingPartExitsLoading &&
          !store.pendingPartExitsError &&
          pagedRows.length === 0 && (
            <div className="pending-part-exits-state">
              {normalizedSearchTerm
                ? "No hay resultados para la búsqueda en los escaneos del mes actual."
                : "No hay repuestos pendientes registrados."}
            </div>
          )}

        {!store.pendingPartExitsLoading &&
          !store.pendingPartExitsError &&
          pagedRows.length > 0 && (
            <div className="pending-table-scroll-wrapper">
            <div
              className="pending-part-exits-table pending-part-exits-table-scanner"
              role="table"
              aria-label="Control de salidas pendientes"
            >
              <div className="pending-part-exits-table-head" role="row">
                <button type="button" onClick={() => handleSort("material_id")}>
                  ID MATERIAL
                </button>
                <button type="button" onClick={() => handleSort("description")}>
                  Descripción
                </button>
                <button
                  type="button"
                  onClick={() => handleSort("quantity_expected")}
                >
                  Cant. registro
                </button>
                <button
                  type="button"
                  onClick={() => handleSort("total_scanned_quantity")}
                >
                  Total material mes
                </button>
                <button
                  type="button"
                  onClick={() => handleSort("difference_quantity")}
                >
                  Dif. mes
                </button>
                <button type="button" onClick={() => handleSort("scanned_at")}>
                  Fecha escaneo
                </button>
                <button
                  type="button"
                  onClick={() => handleSort("operational_status")}
                >
                  Estado operativo
                </button>
                <div role="columnheader">Ubicación ORMA</div>
                <button
                  type="button"
                  onClick={() => handleSort("last_exit_at")}
                >
                  Última salida ORMA
                </button>
                <div role="columnheader">OTs ORMA</div>
                <div role="columnheader">Acciones</div>
              </div>

              {pagedRows.map((row) => (
                <div className="pending-part-exits-row" role="row" key={row.id}>
                  <div
                    className="pending-part-exits-material"
                    role="cell"
                    data-label="ID MATERIAL"
                  >
                    {row.material_id || "-"}
                  </div>
                  <div role="cell" data-label="Descripción">
                    {row.description || "-"}
                  </div>
                  <div role="cell" data-label="Cant. registro">
                    {formatNumber(row.quantity_expected)}
                  </div>
                  <div
                    className="pending-material-totals"
                    role="cell"
                    data-label="Total material mes"
                  >
                    <span className="pending-material-total pending-material-total-scanned">
                      <span className="pending-material-total-label">
                        Escaneado
                      </span>
                      <strong className="pending-material-total-value">
                        {formatNumber(
                          row.total_scanned_quantity ?? row.quantity_expected,
                        )}
                      </strong>
                    </span>
                    <span className="pending-material-total pending-material-total-orma">
                      <span className="pending-material-total-label">ORMA</span>
                      <strong className="pending-material-total-value">
                        {formatNumber(
                          row.total_matched_quantity ??
                            row.matched_exit_quantity,
                        )}
                      </strong>
                    </span>
                  </div>
                  <div role="cell" data-label="Dif. mes">
                    {formatNumber(row.difference_quantity)}
                  </div>
                  <div role="cell" data-label="Fecha escaneo">
                    {formatDateTime(row.scanned_at)}
                  </div>
                  <div role="cell" data-label="Estado operativo">
                    <span
                      className={`pending-status pending-operational-status-${
                        getOperationalStatus(row) || "unknown"
                      }`}
                    >
                      {getOperationalStatusLabel(row)}
                    </span>
                  </div>
                  <div role="cell" data-label="Ubicación ORMA">
                    {renderOrmaLocation(row)}
                  </div>
                  <div role="cell" data-label="Última salida ORMA">
                    {formatDateTime(row.last_exit_at)}
                  </div>
                    <div role="cell" data-label="OTs ORMA">
                      {formatWorkOrders(row.matched_work_orders).length > 0 ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary pending-work-orders-open"
                          onClick={() => openWorkOrdersModal(row)}
                          title={`${formatWorkOrders(row.matched_work_orders).length} OT relacionadas`}
                        >
                          OT's
                          <span className="badge text-bg-primary ms-1">
                            {formatWorkOrders(row.matched_work_orders).length}
                          </span>
                        </button>
                      ) : (
                        "-"
                      )}
                    </div>
                  <div
                    className="pending-part-exits-actions"
                    role="cell"
                    data-label="Acciones"
                  >
                    <button
                      type="button"
                      className="btn btn-sm pending-part-exits-undo btn-danger"
                      onClick={() => handleUndo(row)}
                      disabled={
                        undoingId === row.id ||
                        row.status === "cancelled" ||
                        (isScanner &&
                          getScannedMonthKey(row.scanned_at) !== currentMonthKey) ||
                        store.pendingPartExitsLoading
                      }
                    >
                      <i
                        className="bi bi-arrow-return-left"
                        aria-hidden="true"
                      ></i>
                      {undoingId === row.id ? "Aplicando..." : "Restar"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            </div>
          )}
      </section>
      )}

        {isExitMode && workOrdersModal.open && (
          <div
            className="modal fade show d-block pending-work-orders-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pending_work_orders_modal_title"
            onMouseDown={closeWorkOrdersModal}
            style={{ backgroundColor: "rgba(15, 23, 42, 0.55)" }}
          >
            <div
              className="modal-dialog modal-dialog-centered modal-lg"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <div>
                    <h5
                      className="modal-title"
                      id="pending_work_orders_modal_title"
                    >
                      OT's relacionadas
                    </h5>
                    <p className="mb-0 text-muted">
                      {workOrdersModal.materialId || "-"} ·{" "}
                      {workOrdersModal.description || "Sin descripción"}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Cerrar"
                    onClick={closeWorkOrdersModal}
                  ></button>
                </div>

                <div className="modal-body">
                  <div className="list-group">
                    {workOrdersModal.workOrders.map((workOrder) => (
                      <div
                        className="list-group-item d-flex justify-content-between align-items-center"
                        key={workOrder}
                      >
                        <strong>{workOrder}</strong>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeWorkOrdersModal}
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      <ControlledEntryActionModal
        modal={controlledEntryModal}
        processing={controlledEntryModal.processing}
        onClose={closeControlledEntryModal}
        onQuantityChange={handleControlledEntryQuantityChange}
        onReasonChange={handleControlledEntryReasonChange}
        onConfirm={handleControlledEntrySubmit}
      />

      <QuantityKeypadModal
        open={quantityModal.open}
        mode={quantityModal.mode}
        materialId={
          quantityModal.mode === "register"
            ? materialId.trim()
            : quantityModal.row?.material_id
        }
        description={
          quantityModal.mode === "register"
            ? materialDescription
            : quantityModal.row?.description
        }
        scannedAt={
          quantityModal.mode === "register"
            ? new Date().toISOString()
            : quantityModal.row?.scanned_at
        }
        operationalDayKey={
          quantityModal.mode === "register"
            ? selectedDayKey || todayKey
            : getScannedDayKey(quantityModal.row?.scanned_at)
        }
        registeredQuantity={quantityModal.row?.quantity_expected}
        quantity={quantityModal.quantity}
        error={quantityModal.error}
        processing={isSubmitting || Boolean(undoingId) || isUndoingLatest}
        onAppend={handleQuantityModalAppend}
        onBackspace={handleQuantityModalBackspace}
        onClear={handleQuantityModalClear}
        onCancel={closeQuantityModal}
        onConfirm={handleQuantityModalConfirm}
      />
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        cancelLabel="Cancelar"
        danger
        onConfirm={confirmDialog.onConfirm}
        onCancel={cancelConfirmDialog}
      />
    </div>
  );
};

export default PendingPartExits;
